import type {
  ItemDetails,
  ItemCategoryEntry,
  ItemItemCategory,
  ItemSubCategory,
} from "../types/TestTarget";

function raw_item_sub_category(
  params: Partial<ItemDetails> | undefined
): ItemSubCategory | undefined {
  if (!params) {
    return undefined;
  }
  const legacy = params as Partial<ItemDetails> & { inputType?: unknown };
  const sub = params.itemSubCategory ?? legacy.inputType;
  return sub as ItemSubCategory | undefined;
}

/** UI 用: Item の大分類ラベル */
export const ITEM_CATEGORY_OPTIONS: {
  value: ItemItemCategory;
  label: string;
}[] = [
  { value: "Display", label: "表示（テキスト・画像・エリア等）" },
  { value: "Action", label: "操作（クリック・D&D 等）" },
  { value: "Input", label: "入力（テキスト・数値・範囲・ファイル等）" },
  { value: "Selection", label: "選択（チェック・ラジオ・プルダウン等）" },
];

/** カテゴリごとのサブカテゴリ（itemSubCategory）候補 */
export const SUBCATEGORIES_BY_ITEM_CATEGORY: Record<
  ItemItemCategory,
  { value: ItemSubCategory; label: string }[]
> = {
  Display: [
    { value: "text", label: "テキスト表示" },
    { value: "textarea", label: "複数行テキスト表示" },
    { value: "label", label: "ラベル" },
    { value: "img", label: "画像" },
    { value: "video", label: "動画" },
    { value: "QRcode", label: "QRコード" },
    { value: "barcode", label: "バーコード" },
    { value: "file", label: "画像・メディア表示" },
  ],
  Action: [
    { value: "button", label: "ボタン" },
    { value: "link", label: "リンク" },
  ],
  Input: [
    { value: "text", label: "テキスト" },
    { value: "email", label: "メール" },
    { value: "password", label: "パスワード" },
    { value: "tel", label: "電話" },
    { value: "postcode", label: "郵便番号" },
    { value: "slidebar", label: "スライドバー" },
    { value: "number", label: "数値" },
    { value: "textarea", label: "テキストエリア" },
    { value: "date", label: "日付" },
    { value: "file", label: "ファイル" },
  ],
  Selection: [
    { value: "select", label: "プルダウン" },
    { value: "radio", label: "ラジオ" },
    { value: "checkbox", label: "チェックボックス" },
  ],
};

const LEGACY_HTML_TO_CATEGORY_AND_SUB: Record<
  string,
  { itemCategory: ItemItemCategory; itemSubCategory?: ItemSubCategory }
> = {
  a: { itemCategory: "Action", itemSubCategory: "link" },
  button: { itemCategory: "Action", itemSubCategory: "button" },
  input: { itemCategory: "Input", itemSubCategory: "text" },
  range: { itemCategory: "Input", itemSubCategory: "slidebar" },
  slider: { itemCategory: "Input", itemSubCategory: "slidebar" },
  slidebar: { itemCategory: "Input", itemSubCategory: "slidebar" },
  textarea: { itemCategory: "Input", itemSubCategory: "textarea" },
  select: { itemCategory: "Selection", itemSubCategory: "select" },
  radio: { itemCategory: "Selection", itemSubCategory: "radio" },
  checkbox: { itemCategory: "Selection", itemSubCategory: "checkbox" },
  label: { itemCategory: "Display", itemSubCategory: "label" },
  span: { itemCategory: "Display" },
  div: { itemCategory: "Display" },
  img: { itemCategory: "Display", itemSubCategory: "img" },
  video: { itemCategory: "Display", itemSubCategory: "video" },
};

/**
 * params から itemCategory / itemSubCategory を解決する。
 * 新フィールドがあれば優先し、無ければ旧 type または旧 inputType のみから推論する。
 */
export function resolveItemCategoryAndSubCategory(
  params: Partial<ItemDetails> | undefined
): { itemCategory: ItemItemCategory; itemSubCategory?: ItemSubCategory } {
  if (!params) {
    return { itemCategory: "Action", itemSubCategory: "button" };
  }
  if (params.itemCategory) {
    const sub = raw_item_sub_category(params);
    return { itemCategory: params.itemCategory, itemSubCategory: sub };
  }
  const legacyType = params.type?.toLowerCase();
  if (legacyType && LEGACY_HTML_TO_CATEGORY_AND_SUB[legacyType]) {
    const m = LEGACY_HTML_TO_CATEGORY_AND_SUB[legacyType];
    const subOnly = raw_item_sub_category(params);
    if (subOnly && !m.itemSubCategory) {
      return { itemCategory: m.itemCategory, itemSubCategory: subOnly };
    }
    return {
      itemCategory: m.itemCategory,
      itemSubCategory:
        m.itemSubCategory ?? (subOnly as ItemSubCategory | undefined),
    };
  }
  const onlySub = raw_item_sub_category(params);
  if (onlySub) {
    if (["link", "button"].includes(onlySub)) {
      return { itemCategory: "Action", itemSubCategory: onlySub };
    }
    if (["select", "radio", "checkbox"].includes(onlySub)) {
      return { itemCategory: "Selection", itemSubCategory: onlySub };
    }
    if (["label", "img", "video", "QRcode", "barcode"].includes(onlySub)) {
      return { itemCategory: "Display", itemSubCategory: onlySub };
    }
    if (
      [
        "text",
        "email",
        "password",
        "tel",
        "postcode",
        "slidebar",
        "number",
        "textarea",
        "date",
        "file",
      ].includes(onlySub)
    ) {
      return { itemCategory: "Input", itemSubCategory: onlySub };
    }
  }
  return { itemCategory: "Action", itemSubCategory: "button" };
}

/** params から itemCategory / itemSubCategory の一覧を解決する。複数指定がなければ単数互換値を返す。 */
export function resolveItemCategoryEntries(
  params: Partial<ItemDetails> | undefined
): ItemCategoryEntry[] {
  const rawEntries = (params as { itemCategories?: unknown } | undefined)?.itemCategories;
  if (Array.isArray(rawEntries)) {
    const entries = rawEntries
      .map((entry): ItemCategoryEntry | null => {
        if (!entry || typeof entry !== "object") return null;
        const record = entry as Record<string, unknown>;
        const itemCategory = record.itemCategory;
        const itemSubCategory = record.itemSubCategory;
        const objectType = record.objectType;
        if (
          itemCategory !== "Display" &&
          itemCategory !== "Action" &&
          itemCategory !== "Input" &&
          itemCategory !== "Selection"
        ) {
          return null;
        }
        return {
          ...(typeof objectType === "string" && objectType ? { objectType } : {}),
          itemCategory,
          ...(typeof itemSubCategory === "string" && itemSubCategory
            ? { itemSubCategory: itemSubCategory as ItemSubCategory }
            : {}),
        };
      })
      .filter((entry): entry is ItemCategoryEntry => entry != null);
    if (entries.length > 0) return entries;
  }
  return [resolveItemCategoryAndSubCategory(params)];
}

/** itemSubCategory がカテゴリの候補に含まれるか。未指定は true（Display 等） */
export function isSubCategoryAllowedForCategory(
  category: ItemItemCategory,
  itemSubCategory: ItemSubCategory | undefined
): boolean {
  if (category === "Display") {
    return true;
  }
  if (!itemSubCategory) {
    return false;
  }
  return SUBCATEGORIES_BY_ITEM_CATEGORY[category].some(
    (o) => o.value === itemSubCategory
  );
}

/** カテゴリ変更時に入るよう、候補の先頭を返す */
export function defaultSubCategoryForCategory(
  category: ItemItemCategory
): ItemSubCategory | undefined {
  const list = SUBCATEGORIES_BY_ITEM_CATEGORY[category];
  return list[0]?.value;
}

export function itemFormShowsChoiceFields(
  category: ItemItemCategory,
  itemSubCategory: ItemSubCategory | undefined
): boolean {
  return category === "Selection";
}

export function itemFormShowsDefaultValueField(
  category: ItemItemCategory,
  itemSubCategory: ItemSubCategory | undefined
): boolean {
  if (category === "Selection") {
    return !!itemSubCategory && itemSubCategory !== "checkbox";
  }
  return (
    category === "Input" &&
    !!itemSubCategory &&
    ["text", "email", "password", "tel", "postcode", "slidebar", "number", "textarea", "date"].includes(
      itemSubCategory
    )
  );
}

export function itemFormShowsCheckboxDefaults(
  category: ItemItemCategory,
  itemSubCategory: ItemSubCategory | undefined
): boolean {
  return category === "Selection" && itemSubCategory === "checkbox";
}

export function itemFormShowsTextConstraints(
  category: ItemItemCategory,
  itemSubCategory: ItemSubCategory | undefined
): boolean {
  return (
    category === "Input" &&
    !!itemSubCategory &&
    ["text", "email", "password", "tel", "postcode", "number", "textarea"].includes(
      itemSubCategory
    )
  );
}

/**
 * Item の params を API 送信・永続化する直前に呼ぶ。
 * 既存 JSON に残った旧キーを除去する（merge 後の残留対策）。
 */
export function stripDeprecatedItemParamKeys(params: Record<string, unknown>): void {
  delete params.type;
  delete params.inputType;
  delete params.input_type;
}
